import type {
  AnyCustomFieldDefinition,
  CustomFieldDefinition,
  CustomFieldHelperType,
  CustomFieldType,
} from '@payaca/custom-fields';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

type UseCustomFieldFormOptions = {
  definitions: AnyCustomFieldDefinition[];
  defaultValues?: Record<string, unknown>;
};

export function buildDefaultValues(
  definitions: AnyCustomFieldDefinition[],
  defaultValues?: Record<string, unknown>
): Record<string, unknown> {
  return definitions.reduce(
    (acc, def) => {
      if (def.type === 'fieldset') {
        const fieldsetDef = def;
        const nestedDefaults = defaultValues?.[def.identifier] as
          | Record<string, unknown>
          | undefined;
        acc[def.identifier] = buildDefaultValues(
          fieldsetDef.children,
          nestedDefaults ?? undefined
        );
      } else {
        acc[def.identifier] = defaultValues?.[def.identifier] ?? null;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
}

export function coerceFieldInputToSubmissionValue<T extends CustomFieldType>(
  definition: CustomFieldDefinition<T>,
  value: any
): CustomFieldHelperType[T]['value'] | null {
  if (definition.type === 'number') {
    if (value == null || value === '') return null;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  // A multi-select with nothing chosen is no value at all, so the input can
  // emit the plain array and let the empty case land here. `upload` keeps its
  // empty array for now: its schema admits one, so coercing it would change
  // what gets stored.
  if (definition.type === 'multi-select') {
    if (!Array.isArray(value) || value.length === 0) return null;
    return value;
  }
  return value;
}

function setNestedError(
  obj: Record<string, any>,
  path: string[],
  error: { message: string }
): void {
  let current = obj;
  for (let i = 0; i < path.length - 1; i++) {
    current[path[i]] ??= {};
    current = current[path[i]];
  }
  current[path[path.length - 1]] = error;
}

export function resolveDefinitions(
  definitions: AnyCustomFieldDefinition[],
  values: Record<string, any>,
  path: string[],
  errors: Record<string, any>
): void {
  for (const definition of definitions) {
    const errorPath = [...path, definition.identifier];

    if (definition.type === 'fieldset') {
      const fieldsetDef = definition;
      const nestedValues = values[definition.identifier] as
        | Record<string, any>
        | undefined;
      if (nestedValues && typeof nestedValues === 'object') {
        resolveDefinitions(
          fieldsetDef.children,
          nestedValues,
          errorPath,
          errors
        );
      }
    } else {
      const coercedValue = coerceFieldInputToSubmissionValue(
        definition,
        values[definition.identifier]
      );
      const result = definition.validateValue(coercedValue);

      if (result.isValid) {
        values[definition.identifier] = coercedValue;
      } else {
        setNestedError(errors, errorPath, {
          message: result.validationMessages[0],
        });
      }
    }
  }
}

export function buildResolver(definitions: AnyCustomFieldDefinition[]) {
  return (values: Record<string, any>) => {
    const errors: Record<string, any> = {};

    resolveDefinitions(definitions, values, [], errors);

    if (Object.keys(errors).length > 0) {
      return { values: {}, errors };
    }

    return { values, errors: {} };
  };
}

export function useCustomFieldForm(options: UseCustomFieldFormOptions) {
  const { definitions, defaultValues } = options;

  const builtDefaults = useMemo(
    () => buildDefaultValues(definitions, defaultValues),
    [definitions, defaultValues]
  );
  const resolver = useMemo(() => buildResolver(definitions), [definitions]);

  return useForm({
    defaultValues: builtDefaults,
    resolver,
  });
}
